import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { isRealWithdrawalPspId } from '../helpers'
import { WithdrawalTarget } from '../types'

const safeErrorPayload = (e: any) => {
  const payload: any = { message: e?.message, name: e?.name }
  const r = e?.response
  if (r) payload.response = { status: r?.status, statusText: r?.statusText, data: r?.data }
  if (e?.data) payload.data = e.data
  if (e?.status) payload.status = e.status
  return payload
}

export const useWithdrawalUrlProvider = (params: {
  target?: WithdrawalTarget
  id?: string
  pspId?: string
  walletIdForProvider?: string
  destinationLoading: boolean
  walletPaymentProvider?: WalletPaymentProvider
  setWalletPaymentProvider: (p?: WalletPaymentProvider) => void
  locale: string
}) => {
  const {
    pspId,
    walletIdForProvider,
    destinationLoading,
    walletPaymentProvider,
    setWalletPaymentProvider,
    locale,
  } = params

  const apiClient = useApiClient(ClientApiClient)

  const realPspId = useMemo(
    () => (isRealWithdrawalPspId(pspId) ? (pspId as string) : undefined),
    [pspId]
  )

  const [isUrlProviderLoading, setIsUrlProviderLoading] = useState(false)
  const [urlProviderResolved, setUrlProviderResolved] = useState(false)

  const runIdRef = useRef(0)

  const resolve = useCallback(async () => {
    const runId = ++runIdRef.current

    if (!realPspId) {
      setIsUrlProviderLoading(false)
      setUrlProviderResolved(true)
      return
    }

    if (walletPaymentProvider?.method?.id === realPspId) {
      setIsUrlProviderLoading(false)
      setUrlProviderResolved(true)
      return
    }

    if (destinationLoading) {
      setIsUrlProviderLoading(false)
      setUrlProviderResolved(false)
      return
    }

    if (!walletIdForProvider) {
      setIsUrlProviderLoading(false)
      setUrlProviderResolved(true)
      return
    }

    setIsUrlProviderLoading(true)

    try {
      const methods = await apiClient.getWalletWithdrawalMethods(locale, walletIdForProvider)
      if (runId !== runIdRef.current) return

      const provider = methods.find((m) => m.method.id === realPspId)

      if (provider) {
        setWalletPaymentProvider(provider)
        setUrlProviderResolved(true)
        return
      }

      setUrlProviderResolved(true)
    } catch (e) {
      if (runId !== runIdRef.current) return
      const payload = safeErrorPayload(e)
      setUrlProviderResolved(true)
      console.error('WITHDRAWAL_URL_PROVIDER_RESOLVE_FAILED', payload)
    } finally {
      if (runId === runIdRef.current) {
        setIsUrlProviderLoading(false)
      }
    }
  }, [
    apiClient,
    destinationLoading,
    locale,
    realPspId,
    setWalletPaymentProvider,
    walletIdForProvider,
    walletPaymentProvider?.method?.id,
  ])

  useEffect(() => {
    void resolve()
  }, [resolve])

  useEffect(() => {
    return () => {
      runIdRef.current++
    }
  }, [])

  return { isUrlProviderLoading, urlProviderResolved }
}
